/**
 * @description Função de teste
 * @returns {null} Retorna null
 */
export function test() {
	return null;
}

/**
 * @description Detecta o sistema operacional do usuário
 * @returns {string|null} Nome do sistema operacional detectado ou null se não for possível detectar
 */
export function getOS() {
	const { userAgent } = window.navigator;
	const { platform } = window.navigator;
	const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
	const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
	const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
	let os = null;

	if (macosPlatforms.indexOf(platform) !== -1) {
		os = 'MacOS';
	} else if (iosPlatforms.indexOf(platform) !== -1) {
		os = 'iOS';
	} else if (windowsPlatforms.indexOf(platform) !== -1) {
		os = 'Windows';
	} else if (/Android/.test(userAgent)) {
		os = 'Android';
	} else if (!os && /Linux/.test(platform)) {
		os = 'Linux';
	}

	// @ts-ignore
	document.documentElement.setAttribute('os', os);
	return os;
}

/**
 * @description Verifica se o dispositivo possui um notch (entalhe na tela)
 * @returns {boolean} Verdadeiro se o dispositivo possui notch, falso caso contrário
 */
export const hasNotch = () => {
	const storybook = window.location !== window.parent.location;
	// @ts-ignore
	const iPhone = /iPhone/.test(navigator.userAgent) && !window.MSStream;
	const aspect = window.screen.width / window.screen.height;
	const aspectFrame = window.innerWidth / window.innerHeight;
	return (iPhone && aspect.toFixed(3) === '0.462') || (storybook && aspectFrame.toFixed(3) === '0.462');
};

/**
 * @description Combina múltiplas referências React em uma única função de callback
 * @param {any[]} refs - Array de referências React para serem combinadas
 * @returns {Function} Função que atribui o mesmo valor a todas as referências
 */
export const mergeRefs = (refs: any[]) => {
	return (value: any) => {
		refs.forEach((ref) => {
			if (typeof ref === 'function') {
				ref(value);
			} else if (ref != null) {
				ref.current = value;
			}
		});
	};
};

/**
 * @description Gera uma cor aleatória a partir de uma lista predefinida de cores
 * @returns {string} Nome da cor aleatória selecionada
 */
export const randomColor = () => {
	const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger'];

	const color = Math.floor(Math.random() * colors.length);

	return colors[color];
};

/**
 * @description Formata um número como valor monetário em dólares americanos
 * @param {number} price - O valor numérico a ser formatado
 * @returns {string} Valor formatado como moeda (USD)
 */
export const priceFormat = (price: number) => {
	return price.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
	});
};

/**
 * @description Formata um número de CNPJ brasileiro no formato XX.XXX.XXX/XXXX-XX
 * @param {string} taxId - O número de CNPJ a ser formatado
 * @returns {string|undefined} CNPJ formatado ou undefined se o valor for vazio
 */
export const taxIdFormat = (taxId: string) => {
	if (!taxId) return;

	return taxId.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

/**
 * @description Calcula a média aritmética dos valores em um array
 * @param {any[]} array - Array de números para calcular a média
 * @returns {number} A média dos valores do array
 */
export const average = (array: any[]) => array.reduce((a, b) => a + b) / array.length;

/**
 * @description Calcula a variação percentual entre dois valores
 * @param {number} value1 - Valor atual ou final
 * @param {number} value2 - Valor base ou inicial
 * @returns {number} Percentual de variação entre os valores, com duas casas decimais
 */
export const percent = (value1: number, value2: number) => Number(((value1 / value2 - 1) * 100).toFixed(2));

/**
 * @description Obtém as primeiras letras de cada palavra em um texto
 * @param {string} text - O texto de onde extrair as primeiras letras
 * @param {number} letterCount - Quantidade de letras a retornar (padrão: 2)
 * @returns {string} String contendo as primeiras letras de cada palavra
 */
export const getFirstLetter = (text: string, letterCount = 2): string =>

	// @ts-ignore
	text
		// .replace(/[\u0300-\u036f]/g, '')
		// .toUpperCase()
		.match(/\b(\w)/g)
		.join('')
		.substring(0, letterCount);

/**
 * @description Cria uma versão com debounce de uma função para limitar sua taxa de execução
 * @param {Function} func - A função a ser executada após o tempo de espera
 * @param {number} wait - Tempo de espera em milissegundos (padrão: 1000ms)
 * @returns {Function} Função com debounce aplicado
 */
export const debounce = (func: (arg0: any) => void, wait = 1000) => {
	let timeout: string | number | NodeJS.Timeout | undefined;

	return function executedFunction(...args: any[]) {
		const later = () => {
			clearTimeout(timeout);
			// @ts-ignore
			func(...args);
		};

		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

/**
 * @description Remove todos os caracteres não numéricos de uma string
 * @param {string} text - O texto a ser processado
 * @returns {string} String contendo apenas os dígitos numéricos do texto original
 */
export const onlyNumbers = (text: string) => {
	return text.replace(/\D/g, '');
};

/**
 * @description Verifica se um número de CPF é válido
 * @param {string} cpf - O número de CPF a ser validado
 * @returns {boolean} Verdadeiro se o CPF for válido, falso caso contrário
 */
export const isValidCPF = (cpf: string): boolean => {
	// Remove caracteres não numéricos
	cpf = cpf.replace(/[^\d]+/g, '');

	if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

	let sum = 0;
	let remainder;

	for (let i = 1; i <= 9; i++) sum += parseInt(cpf[i - 1]) * (11 - i);
	remainder = (sum * 10) % 11;
	if (remainder === 10 || remainder === 11) remainder = 0;
	if (remainder !== parseInt(cpf[9])) return false;

	sum = 0;
	for (let i = 1; i <= 10; i++) sum += parseInt(cpf[i - 1]) * (12 - i);
	remainder = (sum * 10) % 11;
	if (remainder === 10 || remainder === 11) remainder = 0;
	if (remainder !== parseInt(cpf[10])) return false;

	return true;
};

/**
 * @description Extrai o primeiro nome de um nome completo
 * @param {string} fullName - O nome completo a ser processado
 * @returns {string} O primeiro nome ou string vazia se o nome estiver vazio
 */
export const getFirstName = (fullName: string): string => {
    const parts = fullName.trim().split(/\s+/);
    return parts[0] || '';
}

/**
 * @description Extrai o último nome de um nome completo
 * @param {string} fullName - O nome completo a ser processado
 * @returns {string} O último nome ou string vazia se o nome estiver vazio
 */
export const getLastName = (fullName: string): string => {
    const parts = fullName.trim().split(/\s+/);
    return parts.length > 0 ? parts[parts.length - 1] : '';
}

/**
 * @description Gera uma senha complexa e segura com caracteres variados
 * @param {number} length - Comprimento da senha a ser gerada (padrão: 12)
 * @returns {string} Senha complexa gerada aleatoriamente
 */
export const generateComplexPassword = (length = 12): string => {
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+~`|}{[]:;?><,./-=';
    const allChars = upper + lower + numbers + symbols;
    let password = '';
    // Garante ao menos um de cada grupo
    password += upper.charAt(Math.floor(Math.random() * upper.length));
    password += lower.charAt(Math.floor(Math.random() * lower.length));
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));
    password += symbols.charAt(Math.floor(Math.random() * symbols.length));
    // Preenche o restante
    for (let i = password.length; i < length; i++) {
        password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }
    // Embaralha os caracteres
    return password
        .split('')
        .sort(() => Math.random() - 0.5)
        .join('');
}

export const generateTempId = () => `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
